/**
 * SiteTypesEditor — 86bc7raat, tenant side.
 *
 * Which of the platform's site types THIS hauler uses. Corbin's decision on the
 * ticket: the tenant-level toggle (`tenant_site_type_settings.is_enabled`) lives here
 * in web-dispatch, the tenant's own admin surface — not in the Paelode console, which
 * owns the catalog itself.
 *
 * Reads GET /v1/site-types/settings and writes PATCH /v1/site-types/settings/:id, both
 * MANAGE_TENANT. Note the read is NOT the same as GET /v1/site-types: that one returns
 * only active + enabled types (it feeds the site pickers), whereas this one must also
 * show what is switched OFF, or there would be no way to switch it back on.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { formatNumber } from '@lodex-pro/shared/number-format';

export interface SiteTypeSetting {
  readonly siteTypeId: string;
  readonly code: string;
  readonly label: string;
  readonly sortOrder: number;
  readonly isEnabled: boolean;
  /** False when this tenant predates the site type and has no settings row yet. */
  readonly hasSettingRow: boolean;
}

const SETTINGS_KEY = ['site-types', 'settings'] as const;

function useSiteTypeSettings(): ReturnType<
  typeof useApiQuery<{ settings: readonly SiteTypeSetting[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ settings: readonly SiteTypeSetting[] }>(opts, '/v1/site-types/settings', {
    queryKey: SETTINGS_KEY,
    staleTime: 15_000,
  });
}

/** One row's toggle. Its own mutation instance, so a pending row disables only itself. */
function ToggleRow({ setting }: { setting: SiteTypeSetting }): React.JSX.Element {
  const opts = useApiOpts();
  const qc = useQueryClient();
  const mutation = useApiMutation<{ isEnabled: boolean }, { ok: boolean }>(
    opts,
    `/v1/site-types/settings/${setting.siteTypeId}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
        // The site pickers read the other endpoint, which this changes the contents of.
        void qc.invalidateQueries({ queryKey: ['site-types'] });
      },
    },
  );

  const flip = (): void => {
    const next = !setting.isEnabled;
    mutation.mutate(
      { isEnabled: next },
      {
        onSuccess: () =>
          toast.success(next ? `${setting.label} enabled` : `${setting.label} hidden`),
        onError: () => toast.error('Could not change that setting'),
      },
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '12px 14px',
        borderBottom: '1px solid var(--color-border-secondary)',
      }}
    >
      <div>
        <div style={{ fontWeight: 500 }}>{setting.label}</div>
        <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
          {setting.isEnabled
            ? 'Available when creating a site.'
            : 'Hidden — existing sites of this type are unaffected.'}
        </div>
      </div>
      <Button type="button" variant="outline" disabled={mutation.isPending} onClick={flip}>
        {mutation.isPending ? 'Saving…' : setting.isEnabled ? 'Hide' : 'Enable'}
      </Button>
    </div>
  );
}

export function SiteTypesEditor(): React.JSX.Element {
  const { data, isLoading, isError, refetch } = useSiteTypeSettings();
  const settings = data?.settings ?? [];
  const enabledCount = settings.filter((s) => s.isEnabled).length;

  return (
    <div>
      {isLoading && (
        <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
          Loading site types…
        </p>
      )}

      {isError && (
        <div style={{ color: 'var(--color-danger)' }}>
          <p style={{ marginBottom: '8px' }}>Couldn&apos;t load your site types.</p>
          <Button
            variant="outline"
            onClick={(): void => {
              void refetch();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <p
            style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}
          >
            {formatNumber(enabledCount)} of {settings.length} types available to your dispatchers.
          </p>
          <div
            style={{
              borderRadius: 'var(--radius-card)',
              overflow: 'hidden',
              backgroundColor: 'var(--color-bg-secondary)',
            }}
          >
            {settings.map((s) => (
              <ToggleRow key={s.siteTypeId} setting={s} />
            ))}
            {settings.length === 0 && (
              <p style={{ padding: '20px', color: 'var(--color-text-tertiary)' }}>
                No site types exist yet. Paelode manages the catalog.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
